import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { useMemo, useRef } from 'react';
import {
  ActivityIndicator,
  Animated,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  type GestureResponderEvent,
  type PressableProps,
} from 'react-native';

import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { playTap } from '@/lib/sounds';

type Variant = 'primary' | 'secondary';

type Props = PressableProps & {
  title: string;
  variant?: Variant;
  loading?: boolean;
  danger?: boolean;
  small?: boolean;
  /** Προαιρετικό εικονίδιο Ionicons αριστερά από το κείμενο. */
  icon?: keyof typeof Ionicons.glyphMap;
  /** Παρακάμπτει το χρώμα primary background / secondary border+text. */
  accentColor?: string;
};

export function Button({
  title,
  variant = 'primary',
  loading = false,
  danger = false,
  small = false,
  icon,
  disabled,
  accentColor,
  onPress,
  ...rest
}: Props) {
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const isDisabled = disabled || loading;
  const scale = useRef(new Animated.Value(1)).current;
  const contentColor =
    variant === 'secondary'
      ? accentColor ?? colors.text
      : colors.textOnPrimary;

  const animateTo = (value: number) =>
    Animated.spring(scale, {
      toValue: value,
      useNativeDriver: Platform.OS !== 'web',
      speed: 50,
      bounciness: 6,
    }).start();

  function handlePress(e: GestureResponderEvent) {
    if (Platform.OS !== 'web') {
      void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    }
    playTap();
    onPress?.(e);
  }

  return (
    <Pressable
      accessibilityRole="button"
      disabled={isDisabled}
      onPress={handlePress}
      onPressIn={() => !isDisabled && animateTo(0.96)}
      onPressOut={() => animateTo(1)}
      {...rest}
    >
      <Animated.View
        style={[
          styles.base,
          small && styles.baseSmall,
          variant === 'primary' ? styles.primary : styles.secondary,
          danger && variant === 'primary' && styles.danger,
          accentColor != null && variant === 'primary' && { backgroundColor: accentColor },
          accentColor != null && variant === 'secondary' && { borderColor: accentColor },
          isDisabled && styles.disabled,
          { transform: [{ scale }] },
        ]}
      >
        {loading ? (
          <ActivityIndicator color={contentColor} />
        ) : (
          <View style={styles.content}>
            {icon && <Ionicons name={icon} size={small ? 15 : 18} color={contentColor} />}
            <Text
              style={[styles.label, small && styles.labelSmall, variant === 'secondary' && styles.labelSecondary]}
              numberOfLines={2}
            >
              {title}
            </Text>
          </View>
        )}
      </Animated.View>
    </Pressable>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    base: {
      minHeight: 52,
      borderRadius: radius.md,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: spacing.lg,
      paddingVertical: spacing.sm,
    },
    baseSmall: {
      minHeight: 38,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.xs,
    },
    content: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
    },
    primary: {
      backgroundColor: colors.primary,
    },
    secondary: {
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.borderStrong,
    },
    danger: {
      backgroundColor: colors.danger,
    },
    disabled: {
      opacity: 0.5,
    },
    label: {
      color: colors.textOnPrimary,
      fontSize: fontSize.md,
      fontWeight: '600',
      textAlign: 'center',
      flexShrink: 1,
    },
    labelSmall: {
      fontSize: fontSize.sm,
    },
    labelSecondary: {
      color: colors.text,
    },
  });
}
